import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import BackArrowIcon from '../../../../../assets/svg/BackArrowIcon';
import { FloatingButton, FloatingButtonShadow } from '../../../../../components/FloatingButton';
import { Modal } from '../../../../../components/Modal';
import { ROUTE_END_LOCATION, ROUTE_STATUS } from '../../../../../constants/Routes';
import * as routing from '../../../../../constants/Routing';
import { STOP_STATUS } from '../../../../../constants/Stops';
import { StopReport } from '../../../../../domain/report/StopReport';
import { Route as RouteDomain } from '../../../../../domain/route/Route';
import { useDiscardPendingReport } from '../../../hooks/pendingReports/useDiscardPendingReport';
import { usePendingReports } from '../../../hooks/pendingReports/usePendingReports';
import { useRetrieveVehicle } from '../../../hooks/useRetrieveVehicle';
import { useCreateStopReport } from '../../hooks/useCreateStopReport';
import { useDiscardReport } from '../../hooks/useDiscardReport';
import { useUpdateRouteStatus } from '../../hooks/useUpdateRouteStatus';
import { RouteFooter } from '../route-stops-footers/RouteFooter';
import { RouteFooterContainer } from '../route-stops-footers/RouteFooterContainer';
import { useSetSelectedStop } from './hooks/useSetSelectedStop';
import { StopDetailsContainer } from './stop-details/StopDetailsContainer';
import { StopCancel } from './stop-report/stop-cancel/StopCancel';
import { StopCompleteContainer } from './stop-report/stop-complete/StopCompleteContainer';
import { StopAutoComplete } from './stop-report/StopAutoComplete';

interface SelectedStopProps {
  isOnline: boolean;
  selectedRoute: RouteDomain;
  setReopenRouteDialogOpen: (open: boolean) => void;
  setDisableClicks: (disable: boolean) => void;
  endRoute: () => void;
  externalLoading?: boolean;
  allStopsCompleted?: boolean;
}

export const SelectedStop = ({
  isOnline,
  selectedRoute,
  setReopenRouteDialogOpen,
  setDisableClicks,
  externalLoading,
  endRoute,
  allStopsCompleted,
}: SelectedStopProps) => {
  const { selectedStopId } = useSetSelectedStop();
  const { data: vehicleData } = useRetrieveVehicle();

  const {
    run: createReport,
    success: reportSuccess,
    isLoading: isReportLoading,
    error: onReportError,
  } = useCreateStopReport();

  const navigate = useNavigate();
  const {
    run: updateStatus,
    isLoading: updateLoading,
    success: updateSuccess,
  } = useUpdateRouteStatus({
    driverRoute: selectedRoute,
  });
  const [startingRoute, setStartingRoute] = useState(false);
  const [disableReportClick, setDisableReportClicks] = useState(false);

  const stop = selectedStopId ? selectedRoute.getStopById(selectedStopId) : undefined;

  const { data: pendingReports } = usePendingReports();
  // Remove Stop Reports
  const {
    run: discardReport,
    isLoading: isReportOnlineLoading,
    success: discardReportOnlineSuccess,
    reset: resetDiscard,
  } = useDiscardReport({
    driverRoute: selectedRoute,
  });
  // Remove Pending Stop Reports
  const {
    run: removePendingReport,
    isLoading: isPendingReportLoading,
    success: removePendingReportSuccess,
    reset: resetPendingReport,
  } = useDiscardPendingReport();
  const [stopReportToDiscard, setStopReportToDiscard] = useState<StopReport | undefined>(undefined);

  useEffect(() => {
    if (stop && reportSuccess) {
      const nextStopId = selectedRoute.stops.find(
        (stop_find) => stop_find.status === STOP_STATUS.pending && stop.id !== stop_find.id
      )?.id;
      setDisableReportClicks(false);
      onReportCreated(nextStopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSuccess]);

  useEffect(() => {
    setDisableReportClicks(false);
  }, [onReportError]);

  const discardOrRemoveSuccess = discardReportOnlineSuccess || removePendingReportSuccess;

  useEffect(() => {
    if (discardOrRemoveSuccess && stopReportToDiscard) {
      setStopReportToDiscard(undefined);
      if (discardReportOnlineSuccess || removePendingReportSuccess) resetDiscard();
      if (removePendingReportSuccess) resetPendingReport();
    }
    if (updateSuccess && startingRoute) {
      setStartingRoute(false);
      setDisableClicks(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discardOrRemoveSuccess, stopReportToDiscard, updateSuccess, startingRoute, setDisableClicks]);

  const onReportCreated = (stopId?: string) => {
    if (stopId) {
      navigate(routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, stopId));
      return;
    }
    if (selectedRoute.end_location?.isValid()) {
      navigate(
        routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, ROUTE_END_LOCATION)
      );
      return;
    }
    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
  };

  const onReportCanceled = (stopId: string) => {
    navigate(routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, stopId));
  };

  if (!stop) {
    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
    return <StopDetailsContainer />;
  }
  return (
    <Routes>
      <Route
        path="/cancel"
        element={
          <>
            {selectedRoute.status === ROUTE_STATUS.inTransit && vehicleData?.project.stop_reports_enabled ? (
              <>
                <StopDetailsContainer selectedRoute={selectedRoute} footer={false} />
                <StopCancel
                  stop={stop}
                  selectedRoute={selectedRoute}
                  onClose={onReportCanceled}
                  onCanceled={onReportCreated}
                />
              </>
            ) : selectedRoute.status === ROUTE_STATUS.inTransit ? (
              <StopAutoComplete
                onEnter={() => {
                  setDisableReportClicks(true);
                  createReport({
                    status: `canceled`,
                    stop,
                  });
                }}
                isLoading={isReportLoading || disableReportClick}
              />
            ) : (
              <Navigate
                replace
                to={routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(
                  `:stopId`,
                  selectedStopId!
                )}
              />
            )}
          </>
        }
      />
      <Route
        path="/complete"
        element={
          <>
            {selectedRoute.status === ROUTE_STATUS.inTransit && vehicleData?.project.stop_reports_enabled ? (
              <>
                <StopDetailsContainer selectedRoute={selectedRoute} footer={false} />
                <StopCompleteContainer
                  stop={stop}
                  selectedRoute={selectedRoute}
                  onClose={onReportCanceled}
                  onComplete={onReportCreated}
                />
              </>
            ) : selectedRoute.status === ROUTE_STATUS.inTransit ? (
              <StopAutoComplete
                onEnter={() => {
                  setDisableReportClicks(true);
                  createReport({
                    status: `completed`,
                    stop,
                  });
                }}
                isLoading={isReportLoading || disableReportClick}
              />
            ) : (
              <Navigate
                replace
                to={routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(
                  `:stopId`,
                  selectedStopId!
                )}
              />
            )}
          </>
        }
      />

      <Route
        path="*"
        element={
          <>
            <FloatingButtonShadow // This is the shadow of the floating button, it has a different z-index to make it disappear with the draggable at full screen
              style={{
                position: `absolute`,
                top: `16px`,
                left: `16px`,
                zIndex: 1000,
                boxShadow: `rgba(0, 0, 0, 0.08) 0px 4px 12px`,
                pointerEvents: `none`,
              }}
            />
            <FloatingButton
              onClick={() => {
                navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
              }}
              style={{
                position: `absolute`,
                top: `16px`,
                left: `16px`,
                zIndex: 1003,
                boxShadow: `none`,
              }}
            >
              <BackArrowIcon
                style={{
                  fill: `${RoutalPalette.grey1}`,
                  width: `25px`,
                  height: `25px`,
                }}
              />
            </FloatingButton>

            <StopDetailsContainer
              allStopsCompleted={allStopsCompleted}
              discardLoading={isReportOnlineLoading || isPendingReportLoading}
              reportLoading={isReportLoading || disableReportClick}
              selectedRoute={selectedRoute}
              endRoute={endRoute}
              onUpdate={(stopId: string, updateType: `complete` | `cancel` | `discard`) => {
                if (updateType === `discard`) {
                  setStopReportToDiscard(selectedRoute.getStopById(stopId)?.report);
                  return;
                }
                if (vehicleData?.project.stop_reports_enabled) {
                  if (updateType === `complete`) {
                    navigate(
                      routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id)
                        .replace(`:stopId`, stopId)
                        .concat(`/complete`)
                    );
                    return;
                  }
                  if (updateType === `cancel`) {
                    navigate(
                      routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id)
                        .replace(`:stopId`, stopId)
                        .concat(`/cancel`)
                    );
                    return;
                  }
                } else {
                  setDisableReportClicks(true);
                  if (updateType === `complete`) {
                    createReport({
                      status: `completed`,
                      stop,
                    });
                  } else if (updateType === `cancel`) {
                    createReport({
                      status: `canceled`,
                      stop,
                    });
                  }
                }
              }}
            />

            {/* In case the route is in transit, the footer will be managed by the StopDetailsContainer & DraggableContainer
                due to the need of creating up to three footers because of the swipe between stops. */}
            {selectedRoute.status !== ROUTE_STATUS.inTransit && (
              <RouteFooterContainer style={{ boxShadow: `none` }}>
                {selectedRoute.status === ROUTE_STATUS.notStarted || startingRoute ? (
                  <RouteFooter
                    isLoading={updateLoading}
                    onClick={() => {
                      if (isOnline) {
                        setStartingRoute(true);
                        setDisableClicks(true);
                        updateStatus({ status: ROUTE_STATUS.inTransit });
                      }
                    }}
                    intlId="driver.route.start_route"
                  />
                ) : null}
                {selectedRoute.status === ROUTE_STATUS.finished ? (
                  <RouteFooter
                    onClick={() => {
                      setReopenRouteDialogOpen(true);
                    }}
                    intlId="driver.route.reopen_route"
                    isLoading={externalLoading}
                  />
                ) : null}
              </RouteFooterContainer>
            )}
            <Modal
              open={
                Boolean(stopReportToDiscard) ||
                isReportOnlineLoading ||
                isPendingReportLoading ||
                discardOrRemoveSuccess
              }
              title={{
                intlId: `driver.stop.discard_report.title`,
              }}
              subtitle={{
                intlId: `driver.stop.discard_report.info`,
              }}
              buttons={[
                {
                  isLoading: isReportOnlineLoading || isPendingReportLoading || discardOrRemoveSuccess,
                  intlId: `driver.stop.discard_report.confirm`,
                  onClick: () => {
                    if (
                      Boolean(stopReportToDiscard) &&
                      !(isReportOnlineLoading || isPendingReportLoading) &&
                      !discardOrRemoveSuccess
                    ) {
                      if (pendingReports.find((report) => report.data.stopId === stopReportToDiscard?.stop_id)) {
                        removePendingReport({
                          stopId: stopReportToDiscard!.stop_id,
                          routeId: selectedRoute.id,
                        });
                      } else {
                        discardReport({ stopReport: stopReportToDiscard! });
                      }
                    }
                  },
                },
                {
                  intlId: `cancel`,
                  isLoading: false,
                  onClick: () => setStopReportToDiscard(undefined),
                  variant: `text`,
                  style: {
                    border: `none`,
                  },
                },
              ]}
              children={<VSpacer medium />}
            />
          </>
        }
      />
    </Routes>
  );
};
